import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function TrainerManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "trainers";

    /* ---------------- COMMON ---------------- */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    /* ---------------- UPLOAD ---------------- */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 5000;
    const PARALLEL = 6;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");
        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const res = await fetch(`${BASE_URL}/api/trainers/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllTrainers();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // const downloadTemplate = async () => {
    //     const res = await fetch(`${BASE_URL}/api/trainers/template`, {
    //         headers: authHeaders,
    //     });
    //     const blob = await res.blob();
    //     const url = window.URL.createObjectURL(blob);
    //     const a = document.createElement("a");
    //     a.href = url;
    //     a.download = "trainer_template.xlsx";
    //     a.click();
    //     window.URL.revokeObjectURL(url);
    // };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/create_trainer_template.xlsx"; // local path
        a.download = "create_trainer_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };


    /* ---------------- TABLE ---------------- */
    const [trainers, setTrainers] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [totalCount, setTotalCount] = useState(null);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setTrainers(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, "/api/trainers");
            const res = await fetch(scopedUrl + (scopedUrl.includes("?") ? "&" : "?") + "page=0&size=100", { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setTrainers(all);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, all, false);
        } catch {
            setTrainers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTrainers = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/trainers"), { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setTrainers(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    // Keep fetchTrainers as alias for backward compat with edit/delete
    const fetchTrainers = fetchAllTrainers;

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.trainers ?? null); })
            .catch(() => {});
    }, []);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return filterRowsForCurrentScope(trainers).filter(
            (t) =>
                t.trainerId?.toLowerCase().includes(q) ||
                t.trainerName?.toLowerCase().includes(q)
            // t.departmentName?.toLowerCase().includes(q) ||
            // t.projectName?.toLowerCase().includes(q)
        );
    }, [trainers, search]);

    const [exporting, setExporting] = useState(false);

    const exportTableExcel = () => {
        if (exporting) return;
        setExporting(true);
        setTimeout(() => {
            try {
                const ws = XLSX.utils.json_to_sheet(filtered);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Trainers");
                XLSX.writeFile(wb, "trainers.xlsx");
            } finally {
                setExporting(false);
            }
        }, 50);
    };

    /* ---------------- EDIT ---------------- */
    const emptyForm = {
        // departmentName: "",
        // projectName: "",
        trainerName: "",
        trainerMobileNumber: "",
        trainerPersonalEmail: "",
        trainerOfficeEmail: "",
        sidhTrId: "",
    };

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const openEdit = (row) => {
        setEditingId(row.trainerId);
        setForm(row);
    };

    const updateTrainer = async () => {
        await fetch(
            `${BASE_URL}/api/trainers?trainerId=${encodeURIComponent(editingId)}`,
            {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify(form),
            }
        );
        flash("ok", "Trainer updated");
        setEditingId(null);
        store.invalidate(CACHE_KEY);
        fetchTrainers();
    };

    const deleteTrainer = async (id) => {
        if (!window.confirm("Deactivate this trainer?")) return;

        await fetch(
            `${BASE_URL}/api/trainers?trainerId=${encodeURIComponent(id)}`,
            {
                method: "DELETE",
                headers: authHeaders,
            }
        );
        flash("ok", "Trainer deactivated");
        store.invalidate(CACHE_KEY);
        fetchTrainers();
    };

    return (
        <div className="cmp-page">
            <div className="cmp-header">
                <div>
                    <h1>Trainer Management</h1>
                    {/* <p>Excel upload, update, edit and export trainers</p> */}
                    <p></p>
                </div>
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card">
                <div className="card-title">1️⃣ Excel Upload / Update</div>
                <div className="grid-3">

                    <input
                        style={{ border: "1px solid rgb(229, 231, 235)", borderRadius: "10px", padding: "10px 12px", background: "rgb(255, 255, 255);" }}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setFile(e.target.files[0])}
                    />
                    <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                        {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                    </button>
                    <button className="btn" onClick={downloadTemplate}>
                        Download Template
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-top">
                    <div className="card-title">2️⃣ Trainers Table</div>
                    <div className="table-actions">
                        <input
                            className="search"
                            placeholder="Search trainer..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="btn" onClick={exportTableExcel} disabled={exporting}>
                            {exporting ? "Processing..." : "Export Excel"}
                        </button>
                    </div>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}

                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Trainer ID</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            {/* <th>Department</th> */}
                            <th>Project</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.slice(0, visibleCount).map((t, i) => (
                            <tr key={t.trainerId}>
                                <td>{i + 1}</td>
                                <td>{t.trainerId}</td>
                                <td>{t.trainerName}</td>
                                <td>{t.trainerMobileNumber}</td>
                                {/* <td>{t.departmentName}</td> */}
                                <td>{t.projectId}</td>
                                <td className="actions">
                                    {/* <button className="btn btn-sm" onClick={() => openEdit(t)}>
                                        Edit
                                    </button> */}
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => deleteTrainer(t.trainerId)}
                                    >
                                        Deactivate
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!filtered.length && (
                            <tr>
                                <td colSpan="7" className="muted">
                                    {loading ? "Loading..." : "No trainers found"}
                                </td>
                            </tr>
                        )}
                        {!allLoaded && !loading && (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center", padding: "14px" }}>
                                    <button className="btn btn-outline" onClick={fetchAllTrainers} disabled={loadingAll}>
                                        {loadingAll ? "Loading all data…" : "Load All Data"}
                                    </button>
                                </td>
                            </tr>
                        )}
                        {allLoaded && filtered.length > visibleCount && (
                            <tr>
                                <td colSpan="7" style={{ textAlign: "center", padding: "14px" }}>
                                    <button
                                        className="btn btn-outline"
                                        onClick={() => setVisibleCount((c) => c + 100)}
                                    >
                                        Show More ({filtered.length - visibleCount} remaining)
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* EDIT MODAL */}
            {editingId &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-title">Edit Trainer</div>

                            {Object.keys(emptyForm).map((k) => (
                                <div className="field" key={k}>
                                    <label>{k}</label>
                                    <input
                                        value={form[k] || ""}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, [k]: e.target.value }))
                                        }
                                    />
                                </div>
                            ))}

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={updateTrainer}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* INLINE CSS */}
            <style>{`
        .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
        .cmp-header h1 { margin:0; font-size:26px; font-weight:900 }
        .cmp-header p { color:#6b7280; font-size:13px }

        .flash { padding:10px; border-radius:10px; margin:12px 0; font-weight:700 }
        .flash-ok { background:#ecfdf5; color:#065f46 }
        .flash-err { background:#fef2f2; color:#991b1b }

        .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
        .card-title { font-weight:900; margin-bottom:10px }

        .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

        .btn { border:1px solid #e5e7eb; background:#fff; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer }
        .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
        .btn-sm { padding:6px 10px; font-size:12px }
        .btn-danger { border-color:#fca5a5; color:#991b1b }

        table { width:100%; border-collapse:collapse }
        th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px }
        th { color:#6b7280; text-align:left }

        .table-actions { display:flex; gap:10px; align-items:center }
        .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .actions { display:flex; gap:8px }
        .muted { color:#6b7280; font-weight:700 }

        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
        .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
        .modal-title { font-size:18px; font-weight:900; margin-bottom:12px }

        .field { display:flex; flex-direction:column; gap:6px; margin-bottom:10px }
        .field label { font-size:12px; color:#6b7280; font-weight:800 }
        .field input { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
      `}</style>
        </div>
    );
}


// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function TrainerManagementPage() {
//     const token = localStorage.getItem("token");

//     /* ---------------- COMMON ---------------- */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     /* ---------------- UPLOAD ---------------- */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select Excel file first");

//         const fd = new FormData();
//         fd.append("file", file);

//         setUploading(true);
//         try {
//             await fetch(`${BASE_URL}/api/trainers/upload`, {
//                 method: "POST",
//                 headers: authHeaders,
//                 body: fd,
//             });
//             flash("ok", "Excel uploaded successfully");
//             setFile(null);
//             fetchTrainers();
//         } catch {
//             flash("err", "Upload failed");
//         } finally {
//             setUploading(false);
//         }
//     };

//     /* ---------------- TEMPLATE ---------------- */
//     const downloadTemplate = async () => {
//         const res = await fetch(`${BASE_URL}/api/trainers/template`, {
//             headers: authHeaders,
//         });
//         const blob = await res.blob();
//         const url = window.URL.createObjectURL(blob);
//         const a = document.createElement("a");
//         a.href = url;
//         a.download = "trainer_template.xlsx";
//         a.click();
//         window.URL.revokeObjectURL(url);
//     };

//     /* ---------------- TABLE ---------------- */
//     const [trainers, setTrainers] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [search, setSearch] = useState("");

//     const fetchTrainers = async () => {
//         setLoading(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/trainers`, {
//                 headers: authHeaders,
//             });
//             const data = await res.json();
//             setTrainers(Array.isArray(data) ? data : []);
//         } catch {
//             setTrainers([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchTrainers();
//     }, []);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return trainers.filter(
//             (t) =>
//                 t.trainerId?.toLowerCase().includes(q) ||
//                 t.trainerName?.toLowerCase().includes(q) ||
//                 t.departmentName?.toLowerCase().includes(q) ||
//                 t.projectName?.toLowerCase().includes(q)
//         );
//     }, [trainers, search]);

//     const exportTableExcel = () => {
//         const ws = XLSX.utils.json_to_sheet(filtered);
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, ws, "Trainers");
//         XLSX.writeFile(wb, "trainers.xlsx");
//     };

//     /* ---------------- EDIT ---------------- */
//     const emptyForm = {
//         trainerId: "",
//         departmentName: "",
//         projectName: "",
//         trainerName: "",
//         trainerMobileNumber: "",
//         trainerPersonalEmail: "",
//         trainerOfficeEmail: "",
//         sidhTrId: "",
//     };

//     const [editing, setEditing] = useState(null);
//     const [form, setForm] = useState(emptyForm);

//     const openEdit = (row) => {
//         setEditing(row.trainerId);
//         setForm(row);
//     };

//     const updateTrainer = async () => {
//         await fetch(
//             `${BASE_URL}/trainers?trainerId=${encodeURIComponent(editing)}`,
//             {
//                 method: "PUT",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(form),
//             }
//         );
//         flash("ok", "Trainer updated");
//         setEditing(null);
//         fetchTrainers();
//     };

//     /* ---------------- DELETE ---------------- */
//     const deleteTrainer = async (id) => {
//         if (!window.confirm("Deactivate this trainer?")) return;

//         await fetch(
//             `${BASE_URL}/trainers?trainerId=${encodeURIComponent(id)}`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Trainer deactivated");
//         fetchTrainers();
//     };

//     return (
//         <div className="cmp-page">
//             <div className="cmp-header">
//                 <div>
//                     <h1>Trainer Management</h1>
//                     <p>Excel upload, update, edit, soft delete</p>
//                 </div>
//             </div>

//             {msg.text && (
//                 <div className={`flash flash-${msg.type}`}>{msg.text}</div>
//             )}

//             {/* 1️⃣ Upload */}
//             <div className="card">
//                 <div className="card-title">1️⃣ Excel Upload / Update</div>
//                 <div className="grid-3">
//                     <input
//                         type="file"
//                         accept=".xlsx,.xls"
//                         onChange={(e) => setFile(e.target.files[0])}
//                     />
//                     <button className="btn btn-primary" onClick={uploadExcel}>
//                         {uploading ? "Uploading..." : "Upload"}
//                     </button>
//                     <button className="btn" onClick={downloadTemplate}>
//                         Download Template
//                     </button>
//                 </div>
//             </div>

//             {/* 2️⃣ Table */}
//             <div className="card">
//                 <div className="card-top">
//                     <div className="card-title">2️⃣ Trainers Table</div>
//                     <div className="table-actions">
//                         <input
//                             className="search"
//                             placeholder="Search trainer..."
//                             value={search}
//                             onChange={(e) => setSearch(e.target.value)}
//                         />
//                         <button className="btn" onClick={exportTableExcel}>
//                             Export Excel
//                         </button>
//                     </div>
//                 </div>

//                 <table>
//                     <thead>
//                         <tr>
//                             <th>ID</th>
//                             <th>Name</th>
//                             <th>Mobile</th>
//                             <th>Department</th>
//                             <th>Project</th>
//                             <th>Actions</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {filtered.map((t) => (
//                             <tr key={t.trainerId}>
//                                 <td>{t.trainerId}</td>
//                                 <td>{t.trainerName}</td>
//                                 <td>{t.trainerMobileNumber}</td>
//                                 <td>{t.departmentName}</td>
//                                 <td>{t.projectName}</td>
//                                 <td className="actions">
//                                     <button className="btn btn-sm" onClick={() => openEdit(t)}>
//                                         Edit
//                                     </button>
//                                     <button
//                                         className="btn btn-sm btn-danger"
//                                         onClick={() => deleteTrainer(t.trainerId)}
//                                     >
//                                         Deactivate
//                                     </button>
//                                 </td>
//                             </tr>
//                         ))}
//                         {!filtered.length && (
//                             <tr>
//                                 <td colSpan="6" className="muted">
//                                     {loading ? "Loading..." : "No trainers found"}
//                                 </td>
//                             </tr>
//                         )}
//                     </tbody>
//                 </table>
//             </div>

//             {/* EDIT MODAL */}
//             {editing &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditing(null)}>
//                         <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
//                             <div className="modal-title">Edit Trainer</div>

//                             {Object.keys(emptyForm).map((k) => (
//                                 <input
//                                     key={k}
//                                     value={form[k] || ""}
//                                     placeholder={k}
//                                     onChange={(e) =>
//                                         setForm((p) => ({ ...p, [k]: e.target.value }))
//                                     }
//                                 />
//                             ))}

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditing(null)}>
//                                     Cancel
//                                 </button>
//                                 <button className="btn btn-primary" onClick={updateTrainer}>
//                                     Save
//                                 </button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )}
//         </div>
//     );
// }
